import React, { useEffect } from 'react';
import './DingoLandingPage.css';

/**
 * Page d'accueil de dingo.tn.
 *
 * Direction : signalétique routière — panneau de direction indigo à filet blanc,
 * lettrage condensé en capitales, jalonnement orange. Le balisage est volontairement
 * statique : aucune donnée dynamique, aucun état. Tout ce qui se voit est visible dès
 * le premier rendu, sans animation d'apparition au défilement.
 *
 * Le référencement (titre, description, Open Graph, données structurées, polices) vit
 * dans public/index.html : il doit être lisible sans exécuter le JavaScript.
 */
export default function DingoLandingPage() {
  useEffect(() => {
    const rail = document.querySelector<HTMLElement>('[data-mobilites-carousel]');
    if (!rail) return;

    const scene = rail.closest<HTMLElement>('.mobilites__scene');
    if (!scene) return;

    const reduire = window.matchMedia('(prefers-reduced-motion: reduce)');
    const originals = Array.from(rail.children) as HTMLElement[];
    let setWidth = 0;
    let x = 0;
    let last = 0;
    let frame = 0;

    originals.forEach((item) => {
      item.dataset.carouselOriginal = 'true';
    });

    [0, 1].forEach(() => {
      originals.forEach((item) => {
        const clone = item.cloneNode(true) as HTMLElement;
        clone.setAttribute('aria-hidden', 'true');
        clone.dataset.carouselClone = 'true';
        rail.appendChild(clone);
      });
    });

    const highlight = () => {
      const sceneBox = scene.getBoundingClientRect();
      const center = sceneBox.left + sceneBox.width / 2;
      let closest: Element | null = null;
      let distance = Infinity;

      Array.from(rail.children).forEach((item) => {
        const box = item.getBoundingClientRect();
        const delta = Math.abs((box.left + box.width / 2) - center);
        if (delta < distance) {
          distance = delta;
          closest = item;
        }
      });

      Array.from(rail.children).forEach((item) => {
        item.classList.toggle('is-active', item === closest);
      });
    };

    const render = () => {
      rail.style.transform = `translate3d(${x}px, 0, 0)`;
      highlight();
    };

    const measure = () => {
      const style = window.getComputedStyle(rail);
      const gap = parseFloat(style.columnGap || style.gap || '0') || 0;
      setWidth = originals.reduce((total, item) => {
        return total + item.getBoundingClientRect().width + gap;
      }, 0);
      x = -setWidth;
      render();
    };

    const tick = (time: number) => {
      if (!last) last = time;
      const delta = Math.min(34, time - last);
      last = time;

      if (!reduire.matches && setWidth > 0) {
        x -= delta * 0.068;
        if (x <= -setWidth * 2) x += setWidth;
        render();
      }

      frame = requestAnimationFrame(tick);
    };

    window.addEventListener('resize', measure);
    measure();
    frame = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('resize', measure);
      Array.from(rail.querySelectorAll('[data-carousel-clone="true"]')).forEach((clone) => {
        clone.remove();
      });
      rail.style.transform = '';
      originals.forEach((item) => {
        item.classList.remove('is-active');
        delete item.dataset.carouselOriginal;
      });
    };
  }, []);

  return (
    <>
      {/* ══ GANTRY ══════════════════════════════════════════════════════════ */}
      <header className="gantry">
        <div className="laize gantry__rang">
          <a className="gantry__logo" href="#haut" aria-label="Dingo — retour en haut">
      <span className="verrou">
              <img className="verrou__marque" src="/marque/boucle.png" alt="" width="127" height="72" />
              <img className="verrou__mot" src="/marque/wordmark-blanc.png" alt="Dingo" width="253" height="56" />
            </span>
          </a>
          <nav className="gantry__nav" aria-label="Sections">
            <a href="#mobilites">Ce qui roule</a>
            <a href="#itineraire">L'itinéraire</a>
            <a href="#proprio">Devenir hôte</a>
            <a href="#questions">Questions</a>
          </nav>
          <a className="gantry__cta" href="#sortie">Télécharger</a>
        </div>
      </header>
      <div className="hachures" role="presentation"></div>

      <main id="haut">

        {/* ══ 1. HÉROS ══════════════════════════════════════════════════════ */}
        <section className="heros">
          <div className="laize heros__grille">
            <div className="heros__colonne">
              <h1 className="heros__titre">
                <span className="cartouche pose-2" style={{ animationDelay: '.1s' }}>Des véhicules à louer près de vous, partout en Tunisie</span>

                <span className="panneau pose-1" style={{ animationDelay: '.16s' }}>
                  <span className="lettrage lettrage--geant panneau__ligne1">N'importe où,<br />n'importe quand.</span>
                  <span className="panneau__filet" role="presentation"></span>
                  <span className="panneau__bas">
                    <span className="lettrage lettrage--haut panneau__ligne2">Go Dingo.</span>
                    <svg className="panneau__chevron" viewBox="0 0 64 40" fill="none" stroke="currentColor"
                         strokeWidth="7" strokeLinecap="square" aria-hidden="true">
                      <path d="M4 20h48M38 6l16 14-16 14"/>
                    </svg>
                  </span>
                </span>
              </h1>

              <p className="heros__phrase pose-2" style={{ animationDelay: '.34s' }}>
                Louez une trottinette, un vélo, un scooter ou une voiture à quelqu'un de votre
                quartier, partout en Tunisie. Ouvrez la carte, repérez ce qui est libre à trois
                rues de chez vous, et partez avec.
              </p>

              <div className="stores">
                <a className="store store--badge" href="https://apps.apple.com/tn/app/dingo/id6746961773" aria-label="Télécharger Dingo sur l'App Store">
                  <img className="store__badge" src="/store-badges/app-store-fr.png" data-src-fr="store-badges/app-store-fr.png" data-src-en="store-badges/app-store-en.png" data-src-ar="store-badges/app-store-ar.png" data-alt-fr="Télécharger dans l'App Store" data-alt-en="Download on the App Store" data-alt-ar="تنزيل من App Store" alt="Télécharger dans l'App Store" loading="lazy" />
                </a>
                <a className="store store--badge" href="https://play.google.com/store/apps/details?id=io.angicia.dingo&amp;pcampaignid=web_share" aria-label="Télécharger Dingo sur Google Play">
                  <img className="store__badge" src="/store-badges/google-play-fr.png" data-src-fr="store-badges/google-play-fr.png" data-src-en="store-badges/google-play-en.png" data-src-ar="store-badges/google-play-ar.png" data-alt-fr="Disponible sur Google Play" data-alt-en="Get it on Google Play" data-alt-ar="احصل عليه من Google Play" alt="Disponible sur Google Play" loading="lazy" />
                </a>
              </div>

            </div>

            {/* Maquette de l'écran carte. Véhicule, tarifs et lieux : exemples. */}
            <div className="tel-hote pose-2" style={{ animationDelay: '.3s' }}>
              <div className="tel" role="img" aria-label="Écran carte de l'application Dingo : véhicules disponibles sur la carte et fiche de véhicule en bas de l'écran.">
                <div className="tel__ecran">
                  <img className="tel__capture" src="/assets/generated/app-carte-accueil.webp" alt="" />
                  <span className="tel__encoche"></span>

                  <div className="carte" aria-hidden="true">
                    <svg viewBox="0 0 300 620" preserveAspectRatio="xMidYMid slice">
                      <rect width="300" height="620" fill="var(--ecran-sol)"/>
                      <path d="M244 0 H300 V184 Q266 152 246 100 Z" fill="var(--ecran-eau)"/>
                      <rect x="8" y="330" width="92" height="78" rx="3" fill="var(--ecran-parc)"/>
                      <rect x="196" y="470" width="118" height="66" rx="3" fill="var(--ecran-parc)"/>
                      <g fill="var(--ecran-bati)">
                        <rect x="66" y="196" width="26" height="18" rx="2"/><rect x="98" y="192" width="18" height="22" rx="2"/>
                        <rect x="176" y="188" width="30" height="16" rx="2"/><rect x="212" y="196" width="16" height="20" rx="2"/>
                        <rect x="58" y="232" width="20" height="24" rx="2"/><rect x="84" y="238" width="30" height="14" rx="2"/>
                        <rect x="180" y="236" width="22" height="22" rx="2"/><rect x="208" y="240" width="26" height="16" rx="2"/>
                        <rect x="62" y="300" width="28" height="16" rx="2"/><rect x="96" y="296" width="16" height="22" rx="2"/>
                        <rect x="186" y="300" width="24" height="20" rx="2"/><rect x="216" y="306" width="20" height="14" rx="2"/>
                        <rect x="118" y="418" width="24" height="18" rx="2"/><rect x="176" y="414" width="28" height="22" rx="2"/>
                        <rect x="64" y="470" width="22" height="20" rx="2"/><rect x="94" y="476" width="28" height="14" rx="2"/>
                        <rect x="122" y="536" width="26" height="18" rx="2"/><rect x="170" y="540" width="20" height="20" rx="2"/>
                      </g>
                      <g fill="none" strokeLinecap="square">
                        <path d="M-10 168 L310 122" stroke="var(--ecran-axe)" strokeWidth="15"/>
                        <path d="M138 -10 L164 630" stroke="var(--ecran-axe)" strokeWidth="13"/>
                        <path d="M-10 450 L310 404" stroke="var(--ecran-route)" strokeWidth="11"/>
                        <path d="M30 -10 L50 630" stroke="var(--ecran-route)" strokeWidth="8"/>
                        <path d="M240 -10 L262 630" stroke="var(--ecran-route)" strokeWidth="8"/>
                        <path d="M-10 288 L310 258" stroke="var(--ecran-route)" strokeWidth="7"/>
                        <path d="M-10 540 L310 512" stroke="var(--ecran-route)" strokeWidth="7"/>
                      </g>
                      <path d="M92 252 C42 300 62 380 128 384 C194 388 222 314 176 268"
                            fill="none" stroke="#F59E0B" strokeWidth="3"
                            strokeDasharray="8 8" strokeLinecap="round" opacity=".8"/>
                    </svg>
                  </div>

                  <span className="rond rond--menu" aria-hidden="true">
                    <svg width="17" height="12" viewBox="0 0 18 12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                      <path d="M1 1h16M1 6h16M1 11h16"/>
                    </svg>
                  </span>
                  <span className="rond rond--gps" aria-hidden="true">
                    <svg width="17" height="17" viewBox="0 0 20 20" fill="currentColor">
                      <path d="M17 3 3 9.4l6 1.6 1.6 6z"/>
                    </svg>
                  </span>

                  <div className="filtres">
                    <span className="filtre filtre--actif">
                      <svg width="10" height="8" viewBox="0 0 12 9" fill="none" stroke="currentColor" strokeWidth="2.2" aria-hidden="true"><path d="M1 4.6 4.3 8 11 1"/></svg>
                      Tous
                    </span>
                    <span className="filtre">Scooters</span>
                    <span className="filtre">Vélos</span>
                    <span className="filtre">Trottinettes</span>
                  </div>

                  <span className="moi" style={{ left: '52%', top: '44%' }}></span>

                  <span className="jalon" style={{ left: '28%', top: '29%', animationDelay: '.62s' }}>
                    <span className="jalon__prix">6 DT</span><span className="jalon__tige"></span>
                  </span>
                  <span className="jalon jalon--vise" style={{ left: '64%', top: '41%', animationDelay: '.74s' }}>
                    <span className="jalon__prix">25 DT</span><span className="jalon__tige"></span>
                  </span>
                  <span className="jalon" style={{ left: '24%', top: '54%', animationDelay: '.86s' }}>
                    <span className="jalon__prix">90 DT</span><span className="jalon__tige"></span>
                  </span>
                  <span className="jalon" style={{ left: '72%', top: '58%', animationDelay: '.98s' }}>
                    <span className="jalon__prix">4 DT</span><span className="jalon__tige"></span>
                  </span>

                  <div className="carrousel">
                    <span className="voisine" aria-hidden="true"></span>

                    <div className="vehicule">
                      <div className="vehicule__vue">
                        <svg viewBox="0 0 260 84" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
                          <rect width="260" height="84" fill="#DED6C6"/>
                          <rect y="52" width="260" height="32" fill="#C9BEA9"/>
                          <rect x="24" y="10" width="72" height="42" rx="3" fill="#D2C8B4"/>
                          <rect x="168" y="4" width="66" height="48" rx="3" fill="#D2C8B4"/>
                          <g fill="#2E2A44">
                            <circle cx="96" cy="60" r="12"/><circle cx="176" cy="60" r="12"/>
                            <path d="M104 60h64l-10-30h-22l-8 12h16l4 18z"/>
                            <path d="M150 30l16-14h16l-4 8h-10l-10 8z"/>
                            <rect x="112" y="34" width="30" height="9" rx="4"/>
                          </g>
                          <circle cx="96" cy="60" r="5" fill="#DED6C6"/>
                          <circle cx="176" cy="60" r="5" fill="#DED6C6"/>
                        </svg>
                        <span className="coeur" aria-hidden="true">
                          <svg width="12" height="11" viewBox="0 0 14 13" fill="none" stroke="currentColor" strokeWidth="1.6">
                            <path d="M7 12C3 9.2 1 7 1 4.6A3.6 3.6 0 0 1 7 2a3.6 3.6 0 0 1 6 2.6C13 7 11 9.2 7 12z"/>
                          </svg>
                        </span>
                        <span className="km">1,8 km</span>
                      </div>

                      <div className="vehicule__rang">
                        <span className="vehicule__nom">Vespa Primavera 125</span>
                        <span className="vehicule__prix">25 DT</span>
                      </div>
                      <div className="vehicule__sous">
                        <span className="vehicule__via">via Yassine</span>
                        <span className="vehicule__inclus">
                          Tout inclus
                          <svg width="10" height="10" viewBox="0 0 12 12" fill="currentColor" aria-hidden="true"><path d="M7 0 1 7h4l0 5 6-7H7z"/></svg>
                          <svg width="10" height="10" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.4" aria-hidden="true"><path d="M6 1l4.5 1.6c0 4-1.6 6.6-4.5 8.4C3.1 9.2 1.5 6.6 1.5 2.6z"/></svg>
                        </span>
                      </div>
                      <div className="vehicule__specs">
                        <span className="spec"><b>2</b>Places</span>
                        <span className="spec"><b>125</b>Cylindrée</span>
                        <span className="spec"><b>A1</b>Permis</span>
                      </div>
                    </div>

                    <span className="voisine" aria-hidden="true"></span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ══ 2. CE QUI ROULE ═══════════════════════════════════════════════ */}
        <section className="mobilites" id="mobilites">
          <div className="laize">
            <div className="direction">
              <div className="direction__entete">
                <h2 className="lettrage lettrage--haut">Choisissez votre façon de partir</h2>
              </div>

              <div className="mobilites__scene" aria-label="Mobilités disponibles sur Dingo">
                <div className="mobilites__rail" data-mobilites-carousel>
                  <article className="mobilite">
                    <img className="mobilite__image" src="/assets/mobilites/kickscooter.webp" alt="" loading="lazy" width="440" height="440" />
                    <h3 className="lettrage lettrage--rang mobilite__nom">Trottinettes</h3>
                    <p className="mobilite__tarif">À partir de 8 DT/h</p>
                  </article>
                  <article className="mobilite">
                    <img className="mobilite__image" src="/assets/mobilites/bike.webp" alt="" loading="lazy" width="440" height="440" />
                    <h3 className="lettrage lettrage--rang mobilite__nom">Vélos</h3>
                    <p className="mobilite__tarif">À partir de 6 DT/h</p>
                  </article>
                  <article className="mobilite">
                    <img className="mobilite__image" src="/assets/mobilites/scooter.webp" alt="" loading="lazy" width="440" height="440" />
                    <h3 className="lettrage lettrage--rang mobilite__nom">Scooters</h3>
                    <p className="mobilite__tarif">À partir de 18 DT/h</p>
                  </article>
                  <article className="mobilite">
                    <img className="mobilite__image" src="/assets/mobilites/motorcycle.webp" alt="" loading="lazy" width="440" height="440" />
                    <h3 className="lettrage lettrage--rang mobilite__nom">Motos</h3>
                    <p className="mobilite__tarif">À partir de 25 DT/h</p>
                  </article>
                  <article className="mobilite">
                    <img className="mobilite__image" src="/assets/mobilites/quad.webp" alt="" loading="lazy" width="440" height="440" />
                    <h3 className="lettrage lettrage--rang mobilite__nom">Quads</h3>
                    <p className="mobilite__tarif">À partir de 30 DT/h</p>
                  </article>
                  <article className="mobilite">
                    <img className="mobilite__image" src="/assets/mobilites/car.webp" alt="" loading="lazy" width="440" height="440" />
                    <h3 className="lettrage lettrage--rang mobilite__nom">Voitures</h3>
                    <p className="mobilite__tarif">À partir de 45 DT/j</p>
                  </article>
                  <article className="mobilite">
                    <img className="mobilite__image" src="/assets/mobilites/van.webp" alt="" loading="lazy" width="440" height="440" />
                    <h3 className="lettrage lettrage--rang mobilite__nom">Vans</h3>
                    <p className="mobilite__tarif">À partir de 65 DT/j</p>
                  </article>
                  <article className="mobilite">
                    <img className="mobilite__image" src="/assets/mobilites/jetski.webp" alt="" loading="lazy" width="440" height="293" />
                    <h3 className="lettrage lettrage--rang mobilite__nom">Jet-skis</h3>
                    <p className="mobilite__tarif">À partir de 90 DT/h</p>
                  </article>
                  <article className="mobilite">
                    <img className="mobilite__image" src="/assets/mobilites/boat.webp" alt="" loading="lazy" width="440" height="293" />
                    <h3 className="lettrage lettrage--rang mobilite__nom">Bateaux</h3>
                    <p className="mobilite__tarif">À partir de 140 DT/h</p>
                  </article>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ══ 3. L'ITINÉRAIRE ═══════════════════════════════════════════════ */}
        <section className="itineraire" id="itineraire">
          <div className="laize">
            <div className="itineraire__entete">
              <p className="cartouche">Comment ça marche</p>
              <h2 className="lettrage lettrage--haut">De la carte à la route</h2>
            </div>

            <div className="trace">
              <svg className="trace__ligne" viewBox="0 0 1000 400" preserveAspectRatio="none" aria-hidden="true">
                <path d="M60 62 C240 62 210 196 400 196 C590 196 560 330 760 330 L960 330"
                      fill="none" stroke="#F59E0B" strokeWidth="3"
                      strokeDasharray="10 9" strokeDashoffset="900" opacity=".8"/>
              </svg>

              <ol className="arrets">
                <li className="arret">
                  <span className="arret__borne">D'abord</span>
                  <h3 className="lettrage lettrage--rang arret__titre">Repérer</h3>
                  <p>La carte montre ce qui est libre autour de vous, avec le tarif à l'heure
                     et à la journée. Quartier vide ? Vous élargissez la recherche d'un geste.</p>
                </li>
                <li className="arret">
                  <span className="arret__borne">Ensuite</span>
                  <h3 className="lettrage lettrage--rang arret__titre">Demander</h3>
                  <p>Vous choisissez le créneau et vous envoyez la demande. Le propriétaire
                     accepte ou refuse ; la réponse arrive en notification.</p>
                </li>
                <li className="arret">
                  <span className="arret__borne">Enfin</span>
                  <h3 className="lettrage lettrage--rang arret__titre">Partir</h3>
                  <p>Vous vous retrouvez, vous photographiez le véhicule ensemble, il vous donne
                     son code de trajet. Vous l'entrez dans l'app : la location démarre.</p>
                </li>
              </ol>
            </div>
          </div>
        </section>

        {/* ══ 4. CÔTÉ PROPRIÉTAIRE ══════════════════════════════════════════ */}
        <section className="proprio" id="proprio">
          <div className="laize proprio__grille">
            <div className="proprio__colonne">
              <p className="cartouche">Gagnez avec votre véhicule</p>
              <h2 className="lettrage lettrage--haut proprio__titre">Transformez vos heures d'arrêt en réservations</h2>
              <p className="proprio__phrase">Publiez votre véhicule en quelques minutes. Vous fixez vos règles,
                 et vous dites oui quand ça vous arrange.</p>

              <ul className="commandes">
                <li><b>Tarif</b><span>à l'heure et, si vous voulez, à la journée.</span></li>
                <li><b>Disponibilités</b><span>les jours et les horaires où le véhicule peut partir.</span></li>
                <li><b>Rendez-vous</b><span>les lieux de prise en charge et de restitution.</span></li>
                <li><b>Validation</b><span>vous dites oui quand ça vous arrange, sinon vous passez.</span></li>
              </ul>

              <a className="store store--clair" href="#sortie">
                <span className="store__txt">
                  <span className="store__sur">Dans l'app</span>
                  <span className="store__nom">Publier une annonce</span>
                </span>
                <svg width="20" height="13" viewBox="0 0 22 14" fill="none" stroke="currentColor" strokeWidth="2.4" aria-hidden="true"><path d="M0 7h18M13 1l6 6-6 6"/></svg>
              </a>
            </div>

            <div className="annonce">
              <img className="annonce__image" src="/assets/generated/annonce-voiture-dingo.webp"
                   alt="Annonce Hyundai i20 avec plaque tunisienne, prix à partir de 18 DT, permis B vérifié et départ à proximité"
                   width="920" height="920" loading="lazy" />
            </div>
          </div>
        </section>


        {/* ══ 6. QUESTIONS ══════════════════════════════════════════════════ */}
        <section className="questions" id="questions">
          <div className="laize">
            <div className="questions__entete">
              <p className="cartouche">Questions fréquentes</p>
              <h2 className="lettrage lettrage--haut">Ce qu'on nous demande avant de télécharger</h2>
            </div>
            <div className="questions__grille">
              <article className="question">
                <h3>Comment louer un véhicule sur Dingo ?</h3>
                <p>Vous ouvrez la carte, vous choisissez ce qui est disponible autour de vous, puis vous envoyez une demande pour le créneau qui vous arrange. Si le propriétaire accepte, vous vous retrouvez sur place, vous faites les photos de départ ensemble, vous entrez le code de trajet et vous partez.</p>
              </article>
              <article className="question">
                <h3>Faut-il un permis pour louer une trottinette ou un vélo ?</h3>
                <p>Non, pas pour une trottinette ni pour un vélo. Pour un scooter, il faut un permis A1 ou A. Pour une voiture, il faut un permis B.</p>
              </article>
              <article className="question">
                <h3>Dans quelles villes de Tunisie Dingo fonctionne-t-il ?</h3>
                <p>Dingo fonctionne partout en Tunisie. Ce que vous voyez sur la carte dépend simplement des véhicules publiés près de vous. À Tunis, La Marsa, Sousse, Sfax, Djerba ou ailleurs, vous pouvez toujours élargir la zone si rien n'apparaît juste à côté.</p>
              </article>
              <article className="question">
                <h3>Comment mettre ma voiture ou mon scooter en location ?</h3>
                <p>Vous créez votre annonce dans l'app avec les photos, le tarif, les disponibilités et les lieux de départ et de retour. Quand quelqu'un fait une demande, vous gardez la main. Vous acceptez, vous refusez, ou vous mettez l'annonce en pause quand ce n'est pas le bon moment.</p>
              </article>
              <article className="question">
                <h3>Que se passe-t-il si le véhicule est abîmé ?</h3>
                <p>Au départ et au retour, les deux personnes prennent des photos du véhicule dans l'app. Comme tout est daté et attaché à la location, il y a une base claire si une rayure ou un dégât est signalé. Dingo conserve ces éléments, mais ne remplace pas une assurance.</p>
              </article>
              <article className="question">
                <h3>C'est quoi le code de trajet ?</h3>
                <p>C'est le code que le propriétaire vous donne au moment du rendez-vous. Vous le saisissez dans l'app pour démarrer la location. Sans ce code, rien ne se lance à distance ou par erreur.</p>
              </article>
              <article className="question">
                <h3>Sur quelles plateformes l'application est-elle disponible ?</h3>
                <p>Dingo est disponible sur iPhone et Android, en arabe, français et anglais.</p>
              </article>
            </div>
          </div>
        </section>

        {/* ══ 7. SORTIE ═════════════════════════════════════════════════════ */}
        <section className="finale" id="sortie">
          <div className="laize">
            <div className="finale__panneau">
              <div className="finale__gauche">
                <p className="finale__derja" lang="ar-TN" dir="rtl">في أي بلاصة، في أي وقت</p>
                <h2 className="lettrage lettrage--haut finale__titre">La ville roule déjà</h2>
                <p className="finale__phrase">De La Marsa à Sousse, de Tunis à Djerba, réservez et partez.</p>
              </div>
              <div className="stores">
                <a className="store store--badge" href="https://apps.apple.com/tn/app/dingo/id6746961773" aria-label="Télécharger Dingo sur l'App Store">
                  <img className="store__badge" src="/store-badges/app-store-fr.png" data-src-fr="store-badges/app-store-fr.png" data-src-en="store-badges/app-store-en.png" data-src-ar="store-badges/app-store-ar.png" data-alt-fr="Télécharger dans l'App Store" data-alt-en="Download on the App Store" data-alt-ar="تنزيل من App Store" alt="Télécharger dans l'App Store" loading="lazy" />
                </a>
                <a className="store store--badge" href="https://play.google.com/store/apps/details?id=io.angicia.dingo&amp;pcampaignid=web_share" aria-label="Télécharger Dingo sur Google Play">
                  <img className="store__badge" src="/store-badges/google-play-fr.png" data-src-fr="store-badges/google-play-fr.png" data-src-en="store-badges/google-play-en.png" data-src-ar="store-badges/google-play-ar.png" data-alt-fr="Disponible sur Google Play" data-alt-en="Get it on Google Play" data-alt-ar="احصل عليه من Google Play" alt="Disponible sur Google Play" loading="lazy" />
                </a>
              </div>
            </div>
          </div>
        </section>
      </main>

      <div className="hachures" role="presentation"></div>
      <footer className="pied">
        <div className="laize pied__rang">
      <span className="verrou verrou--pied">
            <img className="verrou__marque" src="/marque/boucle.png" alt="" width="127" height="72" />
            <img className="verrou__mot" src="/marque/wordmark-blanc.png" alt="Dingo" width="253" height="56" />
          </span>
          <nav className="pied__liens" aria-label="Liens légaux">
            <a href="terms.html">Conditions</a>
            <a href="privacy.html">Confidentialité</a>
            <a href="mailto:support@dingo.tn">support@dingo.tn</a>
          </nav>
          {/* Icônes muettes tant que les profils n'existent pas : un href="#" annonce
              une destination qui n'existe pas et piège la navigation au clavier.
              Remplacer chaque <span> par un <a href="…"> à l'ouverture des comptes. */}
          <div className="pied__sociaux" aria-label="Réseaux sociaux">
            <span className="pied__social" role="img" aria-label="Facebook Dingo" title="Facebook">
              <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M12 2.2a9.8 9.8 0 1 0 0 19.6A9.8 9.8 0 0 0 12 2.2Zm2.7 6h-1.6c-.5 0-.7.3-.7.8v1.2h2.2l-.35 2.3H12.4v5.65H10v-5.65H8.15v-2.3H10V8.8c0-1.95 1.16-3.02 2.94-3.02.86 0 1.6.06 1.76.09V8.2Z"/>
              </svg>
            </span>
            <span className="pied__social pied__social--instagram" role="img" aria-label="Instagram Dingo" title="Instagram">
              <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <rect x="3.2" y="3.2" width="17.6" height="17.6" rx="5" stroke="currentColor" strokeWidth="1.9"/>
                <circle cx="12" cy="12" r="3.8" stroke="currentColor" strokeWidth="1.9"/>
                <circle cx="17.05" cy="6.95" r="1.15" fill="currentColor"/>
              </svg>
            </span>
            <span className="pied__social" role="img" aria-label="TikTok Dingo" title="TikTok">
              <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M15.8 3c.31 2.37 1.62 3.78 3.92 3.93v3.04a6.7 6.7 0 0 1-3.86-1.18v5.66c0 2.87-1.75 5.55-5.44 5.55A5.23 5.23 0 0 1 5 14.75c0-3.2 2.46-5.5 5.73-5.36v3.18c-1.48-.23-2.63.58-2.63 2.12 0 1.43 1.04 2.2 2.25 2.2 1.43 0 2.2-.86 2.2-2.82V3h3.25Z"/>
              </svg>
            </span>
          </div>
        </div>
      </footer>    </>
  );
}
